import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Header5, Header3 } from 'library/components/text-elements/headings';
import { BodyText } from 'library/components/text-elements/text';
import { withUser } from 'util/hoc';
import { MoneyIcon } from 'library/icons';
import LoadingBar from 'library/components/loading-bar/loading-bar';
import RoiChart from 'apps/roi/components/charts/roi-chart';
import { saveRoiSurvey } from 'apps/roi/graph-calls/mutations/save-roi-survey';
import { NotificationRender } from 'library/components/notification';
import {
  StyledActionRow,
  StyledChartWrapper,
  StyledColumn,
  StyledHeaderRow,
  StyledIconWrapper,
  StyledInfoBox,
  StyledProgressBox,
  StyledProgressBoxRow,
  StyledRow,
  StyledSaveButton,
  StyledSideWrapper,
  StyledSurveyColumn,
  StyledTextButton,
  StyledWrapper,
} from './styled-roi-survey';
import { SurveyQuestions } from './components/survey-questions';

const RoiSurvey = ({ user }) => {
  const [answers, setAnswers] = useState({});
  const [programMap, setProgramMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const removeProgram = (valueToRemove) => {
    const newColorMap = { ...programMap };
    delete newColorMap[valueToRemove.id];
    setProgramMap(newColorMap);
    if (answers?.program_ids?.length) {
      setAnswers({
        ...answers,
        program_ids: answers?.program_ids.filter((x) => x !== valueToRemove.id),
      });
    }
  };

  const getAnswerProgress = useCallback(() => {
    const totalNegative = Object.keys(answers).reduce((memo, current) => {
      if (answers[current] === false) {
        return memo + 1;
      }
      return memo;
    }, 0);
    return {
      total: 7 - totalNegative,
      current: Object.keys(answers).length,
    };
  }, [answers]);

  const clearResponses = () => {
    setProgramMap({});
    setAnswers({});
  };

  const handleSave = async () => {
    setSaving(true);
    const { success } = await saveRoiSurvey(answers);
    if (success) {
      NotificationRender(
        'success',
        'You have successfully saved the survey entries.'
      );
    } else {
      NotificationRender(
        'error',
        'There was an error saving the survey inputs. Please check the values and try again.'
      );
    }
    setSaving(false);
  };

  return (
    <StyledWrapper>
      <StyledSideWrapper>
        <StyledHeaderRow>
          <Header3>Salary and Education Investment Comparison Tool</Header3>
        </StyledHeaderRow>
        <StyledInfoBox>
          <StyledIconWrapper>
            <MoneyIcon />
          </StyledIconWrapper>
          <StyledColumn>
            <BodyText>
              Make your education invaluable by planning your future! Learn
              about the <b>Return on Investment (ROI)</b> of a program by
              calculating earnings minus program cost.
            </BodyText>
          </StyledColumn>
        </StyledInfoBox>
        <StyledChartWrapper>
          {!isEmpty(programMap) && (
            <RoiChart
              answers={answers}
              answersCount={Object.keys(answers).length}
              program_ids={Object.keys(programMap)}
              programMap={programMap}
              removeDataCallback={removeProgram}
            />
          )}
        </StyledChartWrapper>
      </StyledSideWrapper>
      <StyledSideWrapper>
        <StyledProgressBox>
          <StyledRow>
            <Header5>Understand your ROI results</Header5>
          </StyledRow>
          <StyledRow>
            <BodyText>
              Receive personalized results by answering a few questions
            </BodyText>
          </StyledRow>
          <StyledProgressBoxRow>
            <LoadingBar
              total={getAnswerProgress().total + 1}
              current={getAnswerProgress().current + 1}
            />
          </StyledProgressBoxRow>
        </StyledProgressBox>
        <StyledSurveyColumn>
          {!isEmpty(programMap) && !isEmpty(user) && (
            <StyledActionRow>
              <StyledSaveButton disabled={saving} onClick={handleSave}>
                Save Responses
              </StyledSaveButton>
              <StyledTextButton onClick={clearResponses}>
                Clear all responses
              </StyledTextButton>
            </StyledActionRow>
          )}
          <StyledColumn>
            <SurveyQuestions
              answers={answers}
              setAnswers={setAnswers}
              programMap={programMap}
              setProgramMap={setProgramMap}
              loading={loading}
              setLoading={setLoading}
            />
          </StyledColumn>
        </StyledSurveyColumn>
      </StyledSideWrapper>
    </StyledWrapper>
  );
};

RoiSurvey.propTypes = {
  user: PropTypes.object,
};

export default withUser(RoiSurvey);
